import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, apiError, BACKEND_URL } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Send, MessageSquare, Mic, Square, Trash2 } from "lucide-react";


function Attachment({ a }) {
  if (!a) return null;
  // Prefer Dropbox (persistent), fall back to local file on backend
  const rel = a.dropbox_url || a.url;
  if (a.too_large || !rel) {
    return (
      <div className="mt-1 flex items-center gap-2 text-[13px] opacity-90">
        📎 <span className="truncate">{a.display_name || a.file_name || a.kind}</span>
        <span className="opacity-70">(apri su Telegram)</span>
      </div>
    );
  }
  const src = `${BACKEND_URL}${rel}`;
  if (a.kind === "photo") {
    return (
      <a href={src} target="_blank" rel="noreferrer" className="block mt-1.5">
        <img
          src={src}
          alt={a.display_name || a.file_name || "foto"}
          className="max-h-64 rounded-lg border border-black/10"
        />
      </a>
    );
  }
  if (a.kind === "video") {
    return (
      <video src={src} controls className="mt-1.5 max-h-64 rounded-lg" />
    );
  }
  if (a.kind === "voice" || a.kind === "audio") {
    return <audio src={src} controls className="mt-1.5 w-full max-w-[260px]" />;
  }
  return (
    <a
      href={src}
      target="_blank"
      rel="noreferrer"
      className="mt-1.5 inline-flex items-center gap-2 underline text-[13px]"
    >
      📎 {a.display_name || a.file_name || a.kind}
    </a>
  );
}

/**
 * ChatPanel
 * Props:
 *   - clientId: string (the client user id this conversation belongs to)
 *   - selfRole: "client" | "staff"
 *   - selfId: string (current user id)
 *   - title: optional header label (default: "Chat con l'agenzia" / "Chat con il cliente")
 *   - emptyText: optional copy when no messages
 *   - height: css height (default 520px)
 */
export default function ChatPanel({
  clientId,
  selfRole,
  selfId,
  title,
  emptyText,
  height = "520px",
}) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const lastCountRef = useRef(0);

  // --- Audio recording state ---
  const [recording, setRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const [uploadingAudio, setUploadingAudio] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordTimerRef = useRef(null);
  const cancelRecordRef = useRef(false);

  const scrollToBottom = useCallback(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/chat/threads/${clientId}/messages`);
      setMessages(data.items || []);
      setLoaded(true);
    } catch (e) {
      // silent — polling
    }
  }, [clientId]);

  const markRead = useCallback(async () => {
    try {
      await api.post(`/chat/threads/${clientId}/mark-read`);
    } catch (e) {
      // ignore
    }
  }, [clientId]);

  // Initial load
  useEffect(() => {
    load();
  }, [load]);

  // Polling every 3s
  useEffect(() => {
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
  }, [load]);

  // Mark read on new messages (from the other side)
  useEffect(() => {
    if (!loaded) return;
    if (messages.length !== lastCountRef.current) {
      lastCountRef.current = messages.length;
      scrollToBottom();
      markRead();
    }
  }, [messages, loaded, scrollToBottom, markRead]);

  const onSend = async (e) => {
    e.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || sending || uploadingAudio) return;
    // Clear and keep focus immediately — the API call runs in background
    setText("");
    inputRef.current?.focus();
    setSending(true);
    try {
      const { data } = await api.post(`/chat/threads/${clientId}/messages`, {
        text: trimmed,
      });
      setMessages((m) => [...m, data.message]);
      requestAnimationFrame(() => {
        scrollToBottom();
        inputRef.current?.focus();
      });
    } catch (e) {
      toast.error(apiError(e));
      // restore the text if send failed so user doesn't lose it
      setText(trimmed);
      inputRef.current?.focus();
    } finally {
      setSending(false);
    }
  };

  // --- Audio recording ---
  const startRecording = async () => {
    if (recording || uploadingAudio) return;
    if (!navigator.mediaDevices || !window.MediaRecorder) {
      toast.error("Il tuo browser non supporta la registrazione audio.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Pick the best supported mime type
      const candidates = [
        "audio/webm;codecs=opus",
        "audio/ogg;codecs=opus",
        "audio/webm",
        "audio/mp4",
      ];
      const mimeType =
        candidates.find((m) => window.MediaRecorder.isTypeSupported(m)) || "";
      const rec = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream);

      audioChunksRef.current = [];
      cancelRecordRef.current = false;
      rec.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) audioChunksRef.current.push(ev.data);
      };
      rec.onstop = async () => {
        // Stop the mic stream tracks
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(recordTimerRef.current);
        recordTimerRef.current = null;
        setRecording(false);
        if (cancelRecordRef.current) {
          audioChunksRef.current = [];
          return;
        }
        const blob = new Blob(audioChunksRef.current, {
          type: rec.mimeType || "audio/webm",
        });
        audioChunksRef.current = [];
        if (blob.size === 0) return;
        await uploadAudioBlob(blob, rec.mimeType || "audio/webm");
      };
      rec.start();
      mediaRecorderRef.current = rec;
      setRecording(true);
      setRecordSeconds(0);
      recordTimerRef.current = setInterval(() => {
        setRecordSeconds((s) => {
          const next = s + 1;
          if (next >= 120) {
            // auto-stop after 2 minutes to keep size reasonable
            try {
              rec.stop();
            } catch (e) {
              /* noop */
            }
          }
          return next;
        });
      }, 1000);
    } catch (e) {
      toast.error("Impossibile accedere al microfono. Controlla i permessi del browser.");
    }
  };

  const stopRecording = () => {
    const rec = mediaRecorderRef.current;
    if (rec && rec.state !== "inactive") {
      try {
        rec.stop();
      } catch (e) {
        /* noop */
      }
    }
  };

  const cancelRecording = () => {
    cancelRecordRef.current = true;
    stopRecording();
    setRecordSeconds(0);
  };

  const uploadAudioBlob = async (blob, mimeType) => {
    setUploadingAudio(true);
    try {
      const ext =
        mimeType.includes("ogg")
          ? "ogg"
          : mimeType.includes("mp4")
          ? "m4a"
          : "webm";
      const form = new FormData();
      form.append("file", blob, `voice.${ext}`);
      const { data } = await api.post(
        `/chat/threads/${clientId}/audio`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setMessages((m) => [...m, data.message]);
      setTimeout(() => {
        scrollToBottom();
        inputRef.current?.focus();
      }, 0);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setUploadingAudio(false);
    }
  };

  const fmtSeconds = (s) => {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${String(r).padStart(2, "0")}`;
  };

  const headerTitle =
    title || (selfRole === "client" ? "Chat con l'agenzia" : "Chat con il cliente");
  const placeholder =
    selfRole === "client"
      ? "Scrivi all'agenzia…"
      : "Rispondi al cliente…";

  return (
    <div
      data-testid="chat-panel"
      className="rounded-3xl border border-[var(--mp-ink)] bg-white shadow-brutal-sm overflow-hidden flex flex-col"
      style={{ height }}
    >
      <div className="px-5 sm:px-6 py-4 border-b border-[var(--mp-border)] flex items-center gap-3 bg-[var(--mp-ink)] text-white">
        <div className="h-8 w-8 rounded-full bg-[var(--mp-yellow)] text-[var(--mp-ink)] grid place-items-center">
          <MessageSquare className="h-4 w-4" />
        </div>
        <div>
          <div className="font-display text-lg tracking-tight leading-none">{headerTitle}</div>
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/60 mt-1">
            Aggiornamento ogni 3 secondi
          </div>
        </div>
      </div>

      <div
        ref={listRef}
        data-testid="chat-messages"
        className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 space-y-3 bg-[var(--mp-dash-bg)]"
      >
        {!loaded ? (
          <div className="text-center text-sm text-[var(--mp-muted)] py-10">
            Caricamento…
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-sm text-[var(--mp-muted)] py-10">
            {emptyText ||
              (selfRole === "client"
                ? "Scrivici qui: ti risponderemo appena possibile."
                : "Nessun messaggio ancora. Apri la conversazione scrivendo qui sotto.")}
          </div>
        ) : (
          messages.map((m) => {
            const mine = String(m.sender_id) === String(selfId);
            return (
              <div
                key={m.id}
                data-testid={`chat-msg-${m.id}`}
                className={`flex ${mine ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[78%] rounded-2xl px-4 py-2.5 text-[15px] leading-relaxed shadow-sm ${
                    mine
                      ? "bg-[var(--mp-ink)] text-white rounded-br-sm"
                      : "bg-white border border-[var(--mp-border)] text-[var(--mp-ink)] rounded-bl-sm"
                  }`}
                >
                  {!mine && (
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[var(--mp-muted)] mb-1 flex items-center gap-1.5">
                      {m.sender_name}
                      {m.source === "telegram" && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[#0088cc] text-white text-[9px] tracking-[0.14em]">
                          TG
                        </span>
                      )}
                    </div>
                  )}
                  {m.text && (
                    <div className="whitespace-pre-wrap break-words">{m.text}</div>
                  )}
                  <Attachment a={m.attachment} />
                  <div
                    className={`mt-1 text-[10px] ${
                      mine ? "text-white/60" : "text-[var(--mp-muted)]"
                    }`}
                  >
                    {new Date(m.created_at).toLocaleString("it-IT", {
                      hour: "2-digit",
                      minute: "2-digit",
                      day: "2-digit",
                      month: "2-digit",
                    })}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <form
        onSubmit={onSend}
        data-testid="chat-form"
        className="p-3 sm:p-4 bg-white border-t border-[var(--mp-border)] flex items-center gap-2"
      >
        {recording ? (
          <div
            data-testid="chat-recording"
            className="flex-1 flex items-center gap-3 h-11 px-4 rounded-full bg-[var(--mp-primary)]/10 border border-[var(--mp-primary)] text-[var(--mp-ink)]"
          >
            <span className="h-2.5 w-2.5 rounded-full bg-[var(--mp-primary)] animate-pulse" />
            <span className="text-sm font-medium">Registrazione in corso…</span>
            <span className="ml-auto font-mono text-sm tabular-nums">
              {fmtSeconds(recordSeconds)}
            </span>
          </div>
        ) : (
          <Input
            ref={inputRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={uploadingAudio ? "Invio audio in corso…" : placeholder}
            data-testid="chat-input"
            className="h-11 rounded-full border-[var(--mp-ink)] flex-1"
            autoFocus
          />
        )}

        {recording ? (
          <>
            <Button
              type="button"
              variant="outline"
              onClick={cancelRecording}
              data-testid="chat-record-cancel"
              className="rounded-full h-11 w-11 p-0 border-[var(--mp-ink)]"
              title="Annulla"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              onClick={stopRecording}
              data-testid="chat-record-stop"
              className="rounded-full h-11 px-5 bg-[var(--mp-primary)] hover:bg-[var(--mp-primary-hover)] text-white"
              title="Invia audio"
            >
              <Square className="h-4 w-4 mr-1" fill="currentColor" strokeWidth={0} />
              <span className="hidden sm:inline">Invia audio</span>
            </Button>
          </>
        ) : (
          <>
            <Button
              type="button"
              onClick={startRecording}
              disabled={sending || uploadingAudio}
              data-testid="chat-record-start"
              variant="outline"
              className="rounded-full h-11 w-11 p-0 border-[var(--mp-ink)]"
              title="Registra audio"
            >
              <Mic className="h-4 w-4" />
            </Button>
            <Button
              type="submit"
              data-testid="chat-send"
              disabled={sending || uploadingAudio || !text.trim()}
              className="rounded-full h-11 px-5 bg-[var(--mp-primary)] hover:bg-[var(--mp-primary-hover)] text-white"
            >
              <Send className="h-4 w-4" />
              <span className="hidden sm:inline ml-1">Invia</span>
            </Button>
          </>
        )}
      </form>
    </div>
  );
}
